export { INVENTORY_SCOPE } from "./navigation.ts";

import type { StateClassKey } from "@tdelivr/design-tokens";

import { type BoardRow, type DetailField } from "./board.ts";

/**
 * What the stock board does to what COM-03 returns, away from any rendering.
 *
 * Built to `22 WP21 Merchant Portal.dc.html`, page `stock`:
 *
 * ```
 * stock:['Stock','COM-03',
 *   'Report stock so customers see a live count. Items without one show as "not reported".',
 *   [['Live counts','162','done','fresh'],['Stale','31','caution','over 30 min'],
 *    ['Not reported','21','unknown',''],['Out of stock','6','closed','']],
 *   ['Product','Count','Updated'],
 *   [['Avocado, Hass','48','2 min ago','done','in stock'],
 *    ['Sukuma wiki','12','40 min ago','caution','stale'],
 *    ['Coriander','—','never','unknown','not reported'],
 *    ['Eggs, tray of 30','0','5 min ago','closed','out of stock']],
 *   'warn','G-36: the service stores only a count and time, so freshness is worked out from age.']
 * ```
 *
 * ## This is the one design file on the platform that names its own absence and prescribes the fix
 *
 * Its closing note is `G-36: the service stores only a count and time, so freshness is worked out
 * from age` — so the design knows COM-03 publishes no freshness flag and tells the build to derive
 * one. Every other absence in this repository is recorded against the design; this one is recorded
 * *by* it. `freshnessOf` below is that instruction carried out, with the threshold taken from the
 * design's own `Stale · 31 · over 30 min` tile rather than chosen.
 *
 * ## What is drawn from data and what is not
 *
 * | drawn                            | source                                                     |
 * | -------------------------------- | ---------------------------------------------------------- |
 * | the rows                         | COM-03's location listing                                  |
 * | `Product` — `Avocado, Hass`      | `sku`. **No product name** — that is COM-02's              |
 * | `Count` — `48`                   | `availableQuantity`                                        |
 * | `Updated` — `2 min ago`          | `updatedAt`, as an age                                     |
 * | `done · in stock`, `caution · stale`, `closed · out of stock` | derived, per G-36 and the design's own note |
 * | `unknown · not reported`         | **not drawable** — see below                               |
 * | the four tiles                   | no listing publishes a total (R-04); no tile row here      |
 *
 * ## `not reported` is a deeper absence than a count, and is not drawn
 *
 * The design's third row is `Coriander · — · never · unknown · not reported`, and its tile reads
 * `Not reported · 21`. **A SKU with no row has never been reported to COM-03 at all** — so "not
 * reported" is a fact about the *product catalogue* rather than about inventory, and this listing
 * cannot see it by construction: it lists rows, and the absence is the lack of one.
 *
 * Drawing it would mean reading COM-02's products for this merchant and subtracting — a second
 * service on one board, which is the fan-out Document 17 WEB-0743 tolerates for one listing per
 * screen and not for a column. So the board draws what has been reported, and says once under the
 * grid that a product absent from it has never been counted. That sentence is what the designed
 * row was for.
 */

/** One availability row, as COM-03's listing publishes it. */
export interface Availability {
  readonly tenantId: string;
  readonly merchantId: string;
  readonly locationId: string;
  readonly sku: string;
  readonly availableQuantity: number;
  readonly version: number;
  readonly updatedAt: string;
  readonly updatedBy: string;
}

/**
 * How long a count may go unreported before the board calls it stale.
 *
 * **Thirty minutes, from the design's own tile** — `Stale · 31 · over 30 min` — rather than chosen
 * here. That matters because the threshold is the whole of the derived state: a number picked in
 * this file would be this console inventing the rule G-36 says to derive, where a number read off
 * the design is the design's rule carried out.
 */
export const STALE_AFTER_MS = 30 * 60 * 1000;

/**
 * The state class and the word, derived as G-36 and the design's own note require.
 *
 * | condition                         | class     | word            | where the word comes from     |
 * | --------------------------------- | --------- | --------------- | ----------------------------- |
 * | quantity is 0                     | `closed`  | `out of stock`  | the design's own row          |
 * | reported over 30 minutes ago      | `caution` | `stale`         | the design's own row and tile |
 * | otherwise                         | `done`    | `in stock`      | the design's own row          |
 *
 * **Out of stock wins over stale**, which is a decision and not an ordering accident. A count of
 * zero reported an hour ago is still a count of zero: the shelf is empty either way, and a merchant
 * told `stale` would go and refresh a number that is already right. The reverse — calling a stale
 * non-zero count `in stock` — is the error this board exists to avoid, which is why staleness is
 * checked second rather than not at all.
 *
 * `now` is injected so a test can place the clock rather than wait for it.
 */
export function freshnessOf(
  row: Availability,
  now: number,
): { classKey: StateClassKey; label: string } {
  if (row.availableQuantity === 0) return { classKey: "closed", label: "out of stock" };
  const reported = Date.parse(row.updatedAt);
  if (!Number.isNaN(reported) && now - reported > STALE_AFTER_MS) {
    return { classKey: "caution", label: "stale" };
  }
  return { classKey: "done", label: "in stock" };
}

/** The states present in what came back, in board order, as the design's own `gridUI` does. */
export function statesPresent(rows: readonly Availability[], now: number): readonly string[] {
  return [...new Set(rows.map((row) => freshnessOf(row, now).label))];
}

/**
 * The design's `Updated` cell: how long ago, in its own grammar.
 *
 * `2 min ago`, `40 min ago` and `5 min ago` are the design's three; `never` is its fourth and
 * belongs to the row this board cannot draw. Seconds are spelled out below ninety, because a
 * merchant who has just reported a count and sees `0 min ago` would wonder whether it took.
 */
export function updatedCell(row: Availability, now: number): string {
  const reported = Date.parse(row.updatedAt);
  if (Number.isNaN(reported)) return row.updatedAt;
  const seconds = Math.max(0, Math.floor((now - reported) / 1000));
  if (seconds < 90) return `${String(seconds)}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 90) return `${String(minutes)} min ago`;
  return `${String(Math.round(minutes / 60))} h ago`;
}

/** One row, with its detail panel. */
export function stockRow(row: Availability, now: number): BoardRow {
  const state = freshnessOf(row, now);

  return {
    id: row.sku,
    cells: [
      {
        text: row.sku,
        /*
         * Mono, because it is a stock-keeping unit and not a name. The design writes
         * `Avocado, Hass`, which is COM-02's `products.name` — resolving it per row means reading
         * the product catalogue alongside the shelf, a second service on one board. Design rule 2
         * sets an identifier in Geist Mono, and this is one.
         */
        mono: true,
        title:
          "The stock-keeping unit COM-03 holds. The design writes a product name — Avocado, Hass — which is COM-02's products.name and would be a second service on this board. See R-35.",
      },
      {
        text: String(row.availableQuantity),
        title: `Reported by ${row.updatedBy} at ${row.updatedAt}.`,
      },
      {
        text: updatedCell(row, now),
        title: `COM-03 publishes updatedAt as ${row.updatedAt}. Freshness is worked out from its age, which is what G-36 and this page's own closing note say to do.`,
      },
    ],
    state: {
      ...state,
      title:
        state.label === "stale"
          ? "Reported more than thirty minutes ago, which is the design's own threshold. The count may be right; nobody has confirmed it recently."
          : state.label === "out of stock"
            ? "The reported count is zero. Out of stock is drawn ahead of stale on purpose: a zero reported an hour ago is still a zero, and a merchant told `stale` would refresh a number that is already right."
            : "Reported within the last thirty minutes.",
    },
    // The design tints its `human` and `failed` rows and this board reaches neither: no derived
    // state here maps to either. Left untinted rather than tinting `out of stock`, which the design
    // draws plain.
    detail: detailFor(row),
    history: historyFor(row),
    // The design's own form glyph for this page.
    glyph: "inventory_2",
  };
}

function detailFor(row: Availability): readonly DetailField[] {
  return [
    { label: "Location", value: row.locationId, mono: true },
    { label: "Reported count", value: String(row.availableQuantity) },
    { label: "Reported by", value: row.updatedBy },
    { label: "Product name", value: "—", absent: true },
  ];
}

function historyFor(row: Availability): readonly DetailField[] {
  return [
    { label: "Last reported", value: row.updatedAt, mono: true },
    { label: "Version", value: `v${String(row.version)}`, mono: true },
  ];
}

/**
 * Why the designed primary action cannot be sent.
 *
 * The design's `Update stock counts` collects a product, a count and optionally a file. **Both of
 * COM-03's writes are `/internal/v1`** — `PUT …/inventory/{sku}` and `POST …/inventory/{sku}/adjust`
 * — which the shared front door does not serve, and ADR 0039 §1 draws that line deliberately: a
 * workload posting over the internal network is not the same caller as a browser.
 *
 * And the form's third field has nowhere to go even if they were public: `Or upload a stock file`
 * is a bulk import, and nothing on the platform accepts one. Each SKU is its own request.
 */
export const UPDATE_REFUSAL =
  "Not available. Both of COM-03's writes are /internal/v1, which the shared front door does not serve (ADR 0039 §1) — and the form's `Or upload a stock file` is a bulk import nothing on the platform accepts.";

/**
 * What this board owes a merchant beyond its rows, which on this board is the designed row it
 * cannot draw.
 *
 * The design's `Coriander · — · never · not reported` and its `Not reported · 21` tile are both the
 * same fact: a product COM-03 has never been told about has no row here. Saying it once in a
 * sentence is the honest form of a row that cannot exist — and a merchant who does not know it
 * would read this board as their whole catalogue.
 */
export function listingNote(rows: readonly Availability[] | undefined): string | undefined {
  if (rows === undefined) return undefined;
  const stale = "Freshness is worked out from each count's age, per G-36 and this page's own note.";
  return (
    `${String(rows.length)} product(s) have a reported count at this branch. ` +
    `A product absent from this board has never been counted: COM-03 holds a row only once ` +
    `stock has been reported, so the design's "not reported" is the lack of a row rather than a ` +
    `state on one, and finding which products those are would mean reading the catalogue here too. ` +
    stale
  );
}
